import React from 'react'
import {
  Controller,
  FormContext,
  useForm,
  useFormContext,
} from 'react-hook-form'
import clsx from 'clsx'
import {
  Box,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Paper,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@material-ui/core'

import {
  AutocompleteTypeAndSearch,
  BaseDialog,
  FileUploadInput,
} from 'components'

import helpers from 'helpers'

import theme from 'theme'
import schema from './schema'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'
import usePermissions from 'hooks/usePermissions'

const LinkedDocumentModal = ({
  open,
  setOpen,
  componentIndex,
  disabledInputs,
  excludedIds = [],
}) => {
  const { WRITTEN_CONTENT_KIND } = constants.documents
  const { PRIVACY_POLICIES } = constants.permissions

  const classes = useStyles()
  const { permitted } = usePermissions()
  const { setValue: setParentValue, getValues: getParentValues } =
    useFormContext()

  const localForm = useForm({
    validationSchema: schema,
    defaultValues: {
      documentType: 'attachment',
      attachments: [],
      privacyPolicyIds: [],
    },
  })

  const { control, watch, errors, handleSubmit } = localForm
  const documentType = watch('documentType')

  const isAttachment = documentType === 'attachment'
  const isLinkedDocument = documentType === 'linkedDocument'

  const evidence = `evidencePlanActions[${componentIndex}].evidence`

  const hasDocumentPermissions =
    permitted(PRIVACY_POLICIES.CREATE) || permitted(PRIVACY_POLICIES.LIST)

  const onSubmit = (data) => {
    const currentAttachments = getParentValues(`${evidence}.attachments`) || []
    const currentPrivacyPolicyIds =
      getParentValues(`${evidence}.privacyPolicyIds`) || []

    const newAttachments = helpers.file.filterExisting(
      data?.attachments,
      currentAttachments,
    )
    const newPrivacyPolicyIds = helpers.file.filterExistingDocuments(
      data?.privacyPolicyIds,
      currentPrivacyPolicyIds,
    )

    setParentValue(`${evidence}.attachments`, [
      ...helpers.file.toArray(currentAttachments),
      ...(newAttachments || []),
    ])
    setParentValue(`${evidence}.privacyPolicyIds`, [
      ...currentPrivacyPolicyIds,
      ...newPrivacyPolicyIds,
    ])
    setOpen(false)
  }

  const handleSearchDocuments = async (inputValue) => {
    try {
      const response = await service.dponet.privacyPolicies.get({
        title: inputValue,
        contentKind: WRITTEN_CONTENT_KIND,
        perPage: 100000,
        minimal: true,
        excludedIds,
        order: 'title ASC',
      })

      return (response?.data?.privacyPolicies || []).map((doc) => ({
        id: doc.id,
        name: doc.title,
      }))
    } catch {
      return []
    }
  }

  return (
    <BaseDialog
      open={open}
      setOpen={setOpen}
      title="Anexar evidências"
      formRef="linked-document-modal-form"
      dialogSize="md"
      fullWidth
      actionButtonText="Selecionar"
      closeButtonText="Cancelar"
    >
      <FormContext {...localForm}>
        <Box
          component="form"
          id="linked-document-modal-form"
          onSubmit={(e) => {
            e.stopPropagation()
            handleSubmit(onSubmit)(e)
          }}
        >
          <FormControl fullWidth>
            <Typography variant="h6" gutterBottom>
              Escolha o tipo de documento para a evidência:
            </Typography>
            <Box>
              <Controller
                name="documentType"
                control={control}
                as={
                  <RadioGroup
                    className={clsx(classes.radioGroup, {
                      [classes.error]:
                        !!errors?.privacyPolicyIds ||
                        !!errors?.documentType ||
                        !!errors?.attachments,
                    })}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      gridGap={theme.spacing(2)}
                    >
                      <Tooltip
                        title={
                          hasDocumentPermissions
                            ? ''
                            : 'Você não possui permissões de documentos da plataforma'
                        }
                      >
                        <Paper
                          variant="outlined"
                          className={clsx(classes.card, {
                            [classes.cardError]:
                              !!errors?.privacyPolicyIds ||
                              !!errors?.documentType,
                          })}
                        >
                          <FormControlLabel
                            value="linkedDocument"
                            control={
                              <Radio
                                className={clsx({
                                  [classes.error]: !!errors?.documentType,
                                })}
                                disabled={!hasDocumentPermissions}
                              />
                            }
                            label="Documento da plataforma"
                            classes={{
                              root: clsx(classes.cardLabel, {
                                [classes.error]: !!errors?.documentType,
                              }),
                            }}
                          />
                          <Collapse in={isLinkedDocument}>
                            <Box px={2} pb={2}>
                              <AutocompleteTypeAndSearch
                                multiple
                                textField={{
                                  label: 'Documentos',
                                  error: !!errors?.privacyPolicyIds,
                                  helperText: errors?.privacyPolicyIds?.message,
                                }}
                                requestToLoadOptions={handleSearchDocuments}
                                name="privacyPolicyIds"
                                disabled={disabledInputs}
                              />
                            </Box>
                          </Collapse>
                        </Paper>
                      </Tooltip>
                      <Paper
                        variant="outlined"
                        className={clsx(classes.card, {
                          [classes.cardError]:
                            !!errors?.attachments || !!errors?.documentType,
                        })}
                      >
                        <FormControlLabel
                          value="attachment"
                          control={
                            <Radio
                              className={clsx({
                                [classes.error]: !!errors?.documentType,
                              })}
                            />
                          }
                          label="Arquivo do computador"
                          classes={{
                            root: clsx(classes.cardLabel, {
                              [classes.error]: !!errors?.documentType,
                            }),
                          }}
                        />
                        <Collapse in={isAttachment}>
                          <Box px={2} pb={2}>
                            <FileUploadInput
                              controlName="attachments"
                              accept={constants.validations.PDF_FILE_TYPE}
                              maxSize={helpers.formatters.file.toBytes(30)}
                              multiple
                              controllerBar
                              hasDownloadFile
                              disabled={disabledInputs}
                            />
                          </Box>
                        </Collapse>
                      </Paper>
                    </Box>
                  </RadioGroup>
                }
              />
            </Box>
            {!!errors?.documentType && (
              <FormHelperText error={!!errors?.documentType}>
                {errors?.documentType?.message}
              </FormHelperText>
            )}
          </FormControl>
        </Box>
      </FormContext>
    </BaseDialog>
  )
}

export default LinkedDocumentModal
